import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';
import { theme } from '../styles/theme';

// Types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  closable?: boolean;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

// Context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Hook
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Styled Components
const ToastContainer = styled.div`
  position: fixed;
  bottom: ${theme.spacing[6]};
  right: ${theme.spacing[6]};
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  max-width: 400px;
  width: 100%;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    left: ${theme.spacing[4]};
    right: ${theme.spacing[4]};
    bottom: ${theme.spacing[4]};
    max-width: none;
  }
`;

const ToastItem = styled.div<{ $type: ToastType; $isExiting?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.lg};
  animation: ${props => props.$isExiting ? slideOut : slideIn} 0.3s ease-out;
  max-width: 100%;
  word-wrap: break-word;
  
  ${({ $type }) => {
    switch ($type) {
      case 'success':
        return `
          background: ${theme.colors.success[100]};
          border: 1px solid ${theme.colors.success[500]};
          color: ${theme.colors.success[700]};
        `;
      case 'error':
        return `
          background: ${theme.colors.error[100]};
          border: 1px solid ${theme.colors.error[500]};
          color: ${theme.colors.error[700]};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warning[100]};
          border: 1px solid ${theme.colors.warning[500]};
          color: ${theme.colors.warning[700]};
        `;
      case 'info':
        return `
          background: ${theme.colors.info[100]};
          border: 1px solid ${theme.colors.info[500]};
          color: ${theme.colors.info[700]};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          border: 1px solid ${theme.colors.border};
          color: ${theme.colors.textPrimary};
        `;
    }
  }}
`;

const ToastIcon = styled.div<{ $type: ToastType }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  
  ${({ $type }) => {
    switch ($type) {
      case 'success':
        return `color: ${theme.colors.success[600]};`;
      case 'error':
        return `color: ${theme.colors.error[600]};`;
      case 'warning':
        return `color: ${theme.colors.warning[600]};`;
      case 'info':
        return `color: ${theme.colors.info[600]};`;
      default:
        return `color: ${theme.colors.textSecondary};`;
    }
  }}
`;

const ToastContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ToastTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.tight};
  margin-bottom: ${theme.spacing[1]};
`;

const ToastMessage = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.normal};
  opacity: 0.9;
`;

const ToastCloseButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  opacity: 0.7;
  transition: all ${theme.transitions.base};
  
  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.1);
  }
`;

const ProgressBar = styled.div<{ $duration: number; $type: ToastType }>`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  border-radius: 0 0 ${theme.borderRadius.lg} ${theme.borderRadius.lg};
  animation: progress ${props => props.$duration}ms linear;
  
  ${({ $type }) => {
    switch ($type) {
      case 'success':
        return `background: ${theme.colors.success[500]};`;
      case 'error':
        return `background: ${theme.colors.error[500]};`;
      case 'warning':
        return `background: ${theme.colors.warning[500]};`;
      case 'info':
        return `background: ${theme.colors.info[500]};`;
      default:
        return `background: ${theme.colors.primary[500]};`;
    }
  }}
  
  @keyframes progress {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
`;

const ToastWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

// Toast Component
const ToastComponent: React.FC<{
  toast: Toast;
  onClose: (id: string) => void;
}> = ({ toast, onClose }) => {
  const [isExiting, setIsExiting] = useState(false);
  
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <XCircle size={20} />;
      case 'warning':
        return <AlertCircle size={20} />;
      case 'info':
        return <Info size={20} />;
      default:
        return <Info size={20} />;
    }
  };
  
  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };
  
  // Auto-close timer
  React.useEffect(() => {
    const duration = toast.duration ?? 7000;
    if (duration > 0) {
      const timer = setTimeout(handleClose, duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, toast.id]);
  
  return (
    <ToastWrapper>
      <ToastItem $type={toast.type} $isExiting={isExiting}>
        <ToastIcon $type={toast.type}>
          {getIcon()}
        </ToastIcon>
        <ToastContent>
          <ToastTitle>{toast.title}</ToastTitle>
          {toast.message && (
            <ToastMessage>{toast.message}</ToastMessage>
          )}
        </ToastContent>
        {(toast.closable ?? true) && (
          <ToastCloseButton onClick={handleClose}>
            <X size={16} />
          </ToastCloseButton>
        )}
      </ToastItem>
      {(toast.duration ?? 7000) > 0 && (
        <ProgressBar 
          $duration={toast.duration ?? 7000} 
          $type={toast.type}
        />
      )}
    </ToastWrapper>
  );
};

// Provider Component
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  const showToast = useCallback((toastData: Omit<Toast, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const toast: Toast = {
      id,
      duration: 7000,
      closable: true,
      ...toastData,
    };
    
    setToasts(prev => [...prev, toast]);
  }, []);

  // Listen for custom toast events from the utility function
  React.useEffect(() => {
    const handleShowToast = (event: CustomEvent) => {
      showToast(event.detail);
    };

    window.addEventListener('show-toast', handleShowToast as EventListener);
    return () => {
      window.removeEventListener('show-toast', handleShowToast as EventListener);
    };
  }, [showToast]);
  
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);
  
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);
  
  const contextValue: ToastContextType = {
    toasts,
    showToast,
    removeToast,
    clearAllToasts,
  };
  
  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer>
        {toasts.map(toast => (
          <ToastComponent
            key={toast.id}
            toast={toast}
            onClose={removeToast}
          />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

// Helper hook for common toast types
export const useToastNotifications = () => {
  const { showToast } = useToast();
  
  return {
    showSuccess: (title: string, message?: string) => 
      showToast({ type: 'success', title, message }),
    
    showError: (title: string, message?: string) => 
      showToast({ type: 'error', title, message }),
    
    showWarning: (title: string, message?: string) => 
      showToast({ type: 'warning', title, message }),
    
    showInfo: (title: string, message?: string) => 
      showToast({ type: 'info', title, message }),
  };
};